<template>
  <div class="settings-page">
    <!-- The page header the phone app's pages carry, back arrow and all. -->
    <PageHeader
      :title="word('shell.settings.title')"
      :show-back="true"
      :back-label="word('shell.back')"
      @back="goBack"
    />

    <div class="settings-page__body">
      <section
        v-for="group in settings.groups"
        :key="group.name"
        class="settings-page__group"
        :data-setting-group="group.name"
      >
        <h2 class="settings-page__group-name">{{ group.name }}</h2>
        <div class="settings-page__card">
          <div
            v-for="setting in group.settings"
            :key="setting.id"
            class="settings-page__setting"
            :data-setting="setting.id"
          >
            <span class="settings-page__setting-name">{{ setting.name }}</span>
            <!-- The control is whatever was registered. It is handed the
                 setting's name, so it can name itself for a screen reader. -->
            <component :is="setting.component" :label="setting.name" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { PageHeader } from '@hds/auto-auction-com-components-mobile';

import { useBackOrHome } from './backOrHome.js';
import { useSettingsStore } from './settingsStore.js';
import { useShellWords } from './shellWords.js';

// The Settings page: one page for a phone and a desktop. It draws what the
// Settings store holds — a group name, a setting name and a control for each —
// and knows nothing about what any setting changes.

const settings = useSettingsStore();
const word = useShellWords();
const goBack = useBackOrHome();
</script>

<style scoped>
.settings-page {
  max-width: 40rem;
  margin: 0 auto;
  color: var(--p-text-color);
  font-family: var(--p-font-body);
}

/* The header brings its own inset; this is the page's, beneath it. */
.settings-page__body {
  padding: 12px 20px 24px;
}

.settings-page__group {
  margin-bottom: 24px;
}

.settings-page__group-name {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}

.settings-page__card {
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
}

.settings-page__setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
}

.settings-page__setting + .settings-page__setting {
  border-top: 1px solid var(--p-content-border-color);
}

.settings-page__setting-name {
  font-size: 15px;
  font-weight: 600;
}

/* A phone: the name above its control, so the control has the full width. */
@media (max-width: 480px) {
  .settings-page__setting {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
}
</style>
